import { HoverCard } from "@mantine/core";
import type { PropsWithChildren } from "react";
import { useMemo } from "react";

import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import type { StaffingClosingStatusesByMonth } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import { StaffingClosingStatusList } from "~/modules/billing/components/StaffingClosingStatusList.tsx";
import { getAggregatedStaffingClosingStatuses } from "~/modules/billing/utils/billingUtils.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";

type Props = PropsWithChildren & {
	closingStatuses: StaffingClosingStatusesByMonth[];
	portalId: string;
	width?: number;
};

export function StaffingClosingStatusesHoverCard({
	children,
	closingStatuses,
	portalId,
	width = 280,
}: Props) {
	const {
		data: allUsersData,
		isLoading: allUsersIsLoading,
		isError: allUsersIsError,
	} = useAllUsers();

	const columnCount = useMemo(
		() => Math.max(1, Math.ceil(getAggregatedStaffingClosingStatuses(closingStatuses).length / 10)),
		[closingStatuses],
	);

	return (
		<HoverCard
			width={width * columnCount}
			withinPortal={true}
			portalProps={{ target: "#" + portalId }}
			openDelay={0}
			closeDelay={20}
			shadow="md"
			position="right"
		>
			<HoverCard.Target>{children}</HoverCard.Target>
			<HoverCard.Dropdown className="rounded-md bg-primary-700 text-sm shadow-md">
				{allUsersIsLoading && (
					<div className="flex justify-center p-3">
						<LoadingSpinner theme="neutral" />
					</div>
				)}
				{allUsersIsError && (
					<p className="p-2 text-danger-500">Fehler beim Laden der Nutzer.</p>
				)}
				{allUsersData && (
					<StaffingClosingStatusList
						closingStatuses={closingStatuses}
						columnCount={columnCount}
						users={allUsersData}
					/>
				)}
			</HoverCard.Dropdown>
		</HoverCard>
	);
}
